import {
  BUSINESS_UNIT_TYPE_REQUIRED_VALUE,
  DATA_SETS_ENUM_TYPES,
  UPSTREAM_TIER_REQUIRED_VALUE,
} from 'constants/dataSets';
import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  ASSET_CATEGORY,
  BUSINESS_UNIT_TYPE,
  CODE,
  CRITICALITY_FROM_DOWNSTREAM_WASTE,
  CRITICALITY_RATING,
  DATE_CLOSED,
  DATE_OPENED,
  END_DATE,
  EQUITY_SHAREHOLDING,
  ESTIMATED,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  LEGAL_NAME,
  NACE_CODE,
  NACE_SECTION,
  NAME,
  NET_BOOK,
  NET_BOOK_CURRENCY,
  NUMBER_OF_EMPLOYEES,
  OWNED_LEASED,
  OWNERSHIP,
  PRIORITY_RATING,
  PRODUCT_CATEGORY_ID,
  PROFIT_CENTER_IDS,
  PROFIT_CENTER_LINKS,
  RELIANCE_ON_FREE_NATURAL_RESOURCES,
  RELIANCE_ON_FREE_SOCIAL_RESOURCES,
  START_DATE,
  SUPPLIER_LINKS,
  UNIQUE_IDENTIFIER,
  UPSTREAM_TIER,
  VOLUME_QTY,
  VOLUME_QTY_UNIT,
} from 'constants/formFields';
import { ESTIMATED_VALUE_OPTIONS } from 'constants/general';
import generateMockEntityIdentity from 'utils/generateMockEntityIdentity';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type {
  TCreateCustomerSchema,
  TCreateOperationsSchema,
  TCreateProductSchema,
  TCreateSupplierVCSchema,
} from 'utils/validators';

type TOptionGroups = Partial<Record<string, IOption[]>>;

interface IBaseMockOptions {
  enumData?: TOptionGroups,
  naceCodesData?: IOption[],
  countryOptions?: IOption[],
}

interface ICustomerMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
}

interface ISupplierMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
}

interface IProductMockOptions {
  enumData?: TOptionGroups,
  profitCenterOptions?: IOption[],
  // Units linkable in the current section, already filtered to relationType by the caller.
  unitLinkOptions?: IOption[],
  relationType?: string | null,
  productCategoriesOptions?: IOption[],
}

interface IBalanceSheetAssetsMockOptions extends IBaseMockOptions {
  profitCenterOptions?: IOption[],
  companyCurrency?: string,
}

const FALLBACK_COUNTRY_OPTION: IOption = {
  value: 'US',
  label: 'United States',
};

const FALLBACK_NACE_SECTION: IOption = {
  value: 'A',
  label: 'A - Agriculture, forestry and fishing',
  divisions: [
    {
      value: '01',
      label: '01 - Crop and animal production, hunting and related service activities',
    },
  ],
};

const FALLBACK_PROFIT_CENTER_OPTION: IOption = {
  value: 'PC-001',
  label: 'PC-001',
};

const FALLBACK_SUPPLIER_OPTION: IOption = {
  value: 'SUP001',
  label: 'SUP001',
};

const FALLBACK_PRODUCT_CATEGORY_OPTION: IOption = {
  value: '1',
  label: 'Primary materials',
};

const getNormalizedOptionText = (value?: string | number | null) => String(value ?? '').toLowerCase();

const getPreferredOption = (options: IOption[] = [], preferredValues: string[] = []) => {
  if (!options.length) {
    return undefined;
  }

  const normalizedPreferredValues = preferredValues.map(getNormalizedOptionText);

  if (!normalizedPreferredValues.length) {
    return options[0];
  }

  return options.find((option) => (
    normalizedPreferredValues.includes(getNormalizedOptionText(option.value))
    || normalizedPreferredValues.includes(getNormalizedOptionText(option.label))
  )) || options[0];
};

const getCountryOption = (countryOptions?: IOption[]) => (
  getPreferredOption(countryOptions, [FALLBACK_COUNTRY_OPTION.value, FALLBACK_COUNTRY_OPTION.label || ''])
  || countryOptions?.[0]
  || FALLBACK_COUNTRY_OPTION
);

const getNaceOptions = (naceCodesData?: IOption[]) => {
  const section = naceCodesData?.[0] || FALLBACK_NACE_SECTION;
  const code = section.divisions?.[0] || FALLBACK_NACE_SECTION.divisions?.[0];

  return {
    section,
    code,
  };
};

const getProfitCenterOption = (profitCenterOptions?: IOption[]) => (
  profitCenterOptions?.[0]
  || FALLBACK_PROFIT_CENTER_OPTION
);

const getSupplierOption = (supplierOptions?: IOption[]) => (
  supplierOptions?.[0]
  || FALLBACK_SUPPLIER_OPTION
);

const getProductCategoryOption = (productCategoriesOptions?: IOption[]) => (
  productCategoriesOptions?.[0]
  || FALLBACK_PRODUCT_CATEGORY_OPTION
);

const getMultiOptions = (options?: IOption[], count: number = 1) => options?.slice(0, count) || [];

export const getCustomerModalMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  countryOptions,
}: ICustomerMockOptions): Partial<TCreateCustomerSchema> => {
  const { section, code } = getNaceOptions(naceCodesData);
  const country = getCountryOption(countryOptions);
  const profitCenterOption = getProfitCenterOption(profitCenterOptions);
  const identity = generateMockEntityIdentity({
    baseName: 'Northwind Retail Group',
    codePrefix: 'CUST',
  });

  return {
    [NAME]: identity.name,
    [UNIQUE_IDENTIFIER]: identity.uniqueIdentifier,
    [CODE]: identity.code,
    [OWNERSHIP]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.ownership]),
    [BUSINESS_UNIT_TYPE]: getPreferredOption(
      enumData?.[DATA_SETS_ENUM_TYPES.businessUnitType],
      [BUSINESS_UNIT_TYPE_REQUIRED_VALUE],
    ),
    [PRIORITY_RATING]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.priorityRating], ['High', 'Medium']),
    [EQUITY_SHAREHOLDING]: '55',
    [FINANCIAL_CONTROL]: '70',
    [DATE_OPENED]: '2021-02-15',
    [DATE_CLOSED]: '2025-12-31',
    [NACE_SECTION]: section,
    [NACE_CODE]: code,
    [EU_TAXONOMY_CODE]: '3.6',
    [ADDRESS_COUNTRY]: country,
    [ADDRESS_STATE]: { value: 'California', label: 'California' },
    [ADDRESS_POST_CODE]: '94105',
    [ADDRESS_CITY]: { value: 'San Francisco', label: 'San Francisco' },
    [ADDRESS_STREET]: 'Market Street',
    [ADDRESS_BUILDING]: '1355',
    [ADDRESS_COORDINATES]: '37.7749, -122.4194',
    [PROFIT_CENTER_LINKS]: [
      {
        profitCenterId: profitCenterOption.value,
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        totalCost: '12.4',
        percentOfTotalCost: 58,
      },
    ],
  };
};

export const getSupplierModalMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  countryOptions,
}: ISupplierMockOptions): Partial<TCreateSupplierVCSchema> => {
  const { section, code } = getNaceOptions(naceCodesData);
  const country = getCountryOption(countryOptions);
  const profitCenterOption = getProfitCenterOption(profitCenterOptions);
  const identity = generateMockEntityIdentity({
    baseName: 'Atlas Components',
    codePrefix: 'SUP',
  });

  return {
    [NAME]: identity.name,
    [UNIQUE_IDENTIFIER]: identity.uniqueIdentifier,
    [CODE]: identity.code,
    [ESTIMATED]: ESTIMATED_VALUE_OPTIONS[0],
    [UPSTREAM_TIER]: getPreferredOption(
      enumData?.[DATA_SETS_ENUM_TYPES.upstreamTier],
      [UPSTREAM_TIER_REQUIRED_VALUE],
    ),
    [OWNERSHIP]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.ownership]),
    [BUSINESS_UNIT_TYPE]: getPreferredOption(
      enumData?.[DATA_SETS_ENUM_TYPES.businessUnitType],
      [BUSINESS_UNIT_TYPE_REQUIRED_VALUE],
    ),
    [EQUITY_SHAREHOLDING]: '40',
    [FINANCIAL_CONTROL]: '65',
    [DATE_OPENED]: '2020-06-01',
    [DATE_CLOSED]: '2026-12-31',
    [NACE_SECTION]: section,
    [NACE_CODE]: code,
    [EU_TAXONOMY_CODE]: '5.3',
    [ADDRESS_COUNTRY]: country,
    [ADDRESS_STATE]: { value: 'Illinois', label: 'Illinois' },
    [ADDRESS_POST_CODE]: '60601',
    [ADDRESS_CITY]: { value: 'Chicago', label: 'Chicago' },
    [ADDRESS_STREET]: 'Lake Street',
    [ADDRESS_BUILDING]: '225',
    [ADDRESS_COORDINATES]: '41.8781, -87.6298',
    [PROFIT_CENTER_LINKS]: [
      {
        profitCenterId: profitCenterOption.value,
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        totalCost: '8.8',
        percentOfTotalCost: 41,
      },
    ],
  };
};

export const getProductModalMockValues = ({
  enumData,
  profitCenterOptions,
  unitLinkOptions,
  relationType,
  productCategoriesOptions,
}: IProductMockOptions): Partial<TCreateProductSchema> => {
  const profitCenterOption = getProfitCenterOption(profitCenterOptions);
  const linkOption = getSupplierOption(unitLinkOptions);
  const identity = generateMockEntityIdentity({
    baseName: 'Recycled Aluminum Sheet',
    codePrefix: 'PRD',
  });

  return {
    [NAME]: identity.name,
    [UNIQUE_IDENTIFIER]: identity.uniqueIdentifier,
    [CODE]: identity.code,
    [PRODUCT_CATEGORY_ID]: getProductCategoryOption(productCategoriesOptions),
    // Only the section's own unit type is linkable, so without one there is nothing to fill.
    [SUPPLIER_LINKS]: relationType
      ? [{ relationUID: linkOption.value, relationType }]
      : [],
    [UPSTREAM_TIER]: getPreferredOption(
      enumData?.[DATA_SETS_ENUM_TYPES.upstreamTier],
      [UPSTREAM_TIER_REQUIRED_VALUE],
    ),
    [DATE_OPENED]: '2024-01-10',
    [DATE_CLOSED]: '2025-01-10',
    [VOLUME_QTY]: '240',
    [VOLUME_QTY_UNIT]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.volumeQtyUnit]),
    [RELIANCE_ON_FREE_NATURAL_RESOURCES]: getMultiOptions(
      enumData?.[DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources],
      2,
    ),
    [RELIANCE_ON_FREE_SOCIAL_RESOURCES]: getMultiOptions(
      enumData?.[DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources],
      1,
    ),
    [CRITICALITY_RATING]: getPreferredOption(
      enumData?.[DATA_SETS_ENUM_TYPES.criticalityRating],
      ['Medium', 'High'],
    ),
    [CRITICALITY_FROM_DOWNSTREAM_WASTE]: '22',
    [PROFIT_CENTER_LINKS]: [
      {
        profitCenterId: profitCenterOption.value,
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        totalCost: '5.7',
        percentOfTotalCost: 32,
        expectedGrowthDecline: 12,
      },
    ],
  };
};

export const getBalanceSheetAssetsModalMockValues = ({
  enumData,
  naceCodesData,
  profitCenterOptions,
  companyCurrency,
  countryOptions,
}: IBalanceSheetAssetsMockOptions): Partial<TCreateOperationsSchema> => {
  const { section, code } = getNaceOptions(naceCodesData);
  const country = getCountryOption(countryOptions);
  const profitCenterOption = getProfitCenterOption(profitCenterOptions);
  const identity = generateMockEntityIdentity({
    baseName: 'Northern Distribution Hub',
    codePrefix: 'BSA',
  });

  return {
    [NAME]: identity.name,
    [UNIQUE_IDENTIFIER]: identity.uniqueIdentifier,
    [LEGAL_NAME]: 'Northern Distribution Hub LLC',
    [OWNERSHIP]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.ownership]),
    [EQUITY_SHAREHOLDING]: '100',
    [FINANCIAL_CONTROL]: '100',
    [ASSET_CATEGORY]: getMultiOptions(enumData?.[DATA_SETS_ENUM_TYPES.assetCategory], 2),
    [OWNED_LEASED]: getPreferredOption(enumData?.[DATA_SETS_ENUM_TYPES.ownedLeased], ['Owned']),
    [NUMBER_OF_EMPLOYEES]: '145',
    [DATE_OPENED]: '2019-04-01',
    [DATE_CLOSED]: '2035-12-31',
    [NACE_SECTION]: section,
    [NACE_CODE]: code,
    [EU_TAXONOMY_CODE]: '6.15',
    [PROFIT_CENTER_IDS]: [profitCenterOption],
    [START_DATE]: '2024-01-01',
    [END_DATE]: '2024-12-31',
    [NET_BOOK]: '18.6',
    [NET_BOOK_CURRENCY]: companyCurrency || 'USD',
    [ADDRESS_COUNTRY]: country,
    [ADDRESS_STATE]: { value: 'Texas', label: 'Texas' },
    [ADDRESS_POST_CODE]: '75001',
    [ADDRESS_CITY]: { value: 'Addison', label: 'Addison' },
    [ADDRESS_STREET]: 'Belt Line Road',
    [ADDRESS_BUILDING]: '500',
    [ADDRESS_COORDINATES]: '32.9618, -96.8292',
  };
};
